// Content for the exam landing page (/exams/:id).
//
// Exam-specific content comes from the API (ExamLandingResponse): title, lead
// (description), highlights, coverage summary, modules, instructors, ...
// Everything here is the fixed wording shared by every exam, plus the text
// that is generated from the exam data (which modules are handwritten, how
// many modules there are, who grades it, whether there is a raffle).

const MODULE_COUNT_WORDS = { 2: 'двата', 3: 'трите', 4: 'четирите', 5: 'петте' };

const joinWithAnd = (items) => (
    items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} и ${items[items.length - 1]}`
);

// [2] -> "Модул 2", [2, 3] -> "Модули 2 и 3"
const formatModules = (numbers) => (
    numbers.length === 1 ? `Модул ${numbers[0]}` : `Модули ${joinWithAnd(numbers)}`
);

const lowerFirst = (text) => text.charAt(0).toLowerCase() + text.slice(1);

const ROLE_LABELS = {
    CREATOR: 'Създател и оценяващ',
    GRADER: 'Оценяващ',
};

export const buildExamContent = (exam) => {
    const sections = [...(exam.sections || [])].sort((a, b) => a.order_index - b.order_index);
    const moduleCount = sections.length;
    const handwrittenNumbers = sections
        .map((section, i) => (section.is_handwritten ? i + 1 : null))
        .filter(Boolean);
    const hasHandwritten = handwrittenNumbers.length > 0;
    const handwrittenModules = hasHandwritten ? formatModules(handwrittenNumbers) : '';
    const coverage = exam.coverage_summary?.trim();
    const instructors = exam.instructors || [];

    // Topics are numbered across the whole exam: Тема 1, 2 in module 1, Тема 3 in module 2, ...
    let topicNumber = 0;
    const modules = sections.map((section) => ({
        title: section.title,
        topics: (section.topics || []).map((text) => ({ label: `Тема ${++topicNumber}`, text })),
        durationMinutes: section.duration_minutes,
        description: section.description,
        handwritten: section.is_handwritten,
    }));

    const teachers = instructors.map((instructor) => ({
        name: instructor.name.replace(/\s+/g, ' ').trim(),
        role: ROLE_LABELS[instructor.role],
        image: instructor.image_url,
        // Bio written for this exam (plain text, blank line = new paragraph),
        // otherwise the profile description, which is HTML.
        bio: instructor.bio ? instructor.bio.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean) : [],
        bioHtml: instructor.bio ? null : instructor.description,
        // The consultation course picked in admin, otherwise the instructor's profile.
        courseUrl: instructor.course_id ? `/course/${instructor.course_id}` : `/instructor/${instructor.id}`,
    }));

    const faq = [
        {
            question: 'Има ли определена дата и час?',
            answer: 'Не. Пробната матура не е събитие на живо и няма фиксирана дата или час. Ученикът я решава онлайн, когато му е удобно.',
        },
        {
            question: 'Кога може да започне ученикът?',
            answer: 'След успешна покупка и получаване на достъп — в удобно за него време.',
        },
        coverage && {
            question: 'Какъв материал обхваща?',
            answer: moduleCount > 1
                ? `${coverage}. Матурата е структурирана в ${moduleCount} модула.`
                : `${coverage}.`,
        },
        hasHandwritten && {
            question: 'Защо трябва да се качват снимки?',
            answer: `В ${lowerFirst(handwrittenModules)} има задачи, които ученикът решава на ръка. Той ги снима и качва в платформата, за да бъдат проверени от преподавател.`,
        },
        hasHandwritten && {
            question: 'Какво става, ако снимката е нечетлива?',
            answer: 'Ако написаното на снимката не може да бъде разчетено от проверяващия, отговорът няма да може да бъде коректно оценен. Затова е важно всички снимки да бъдат ясни, добре осветени и четливи. При нечетливи снимки работата може да бъде анулирана.',
        },
        {
            question: 'Кога получаваме резултата?',
            answer: 'Автоматично оценяваните задачи показват резултат веднага след финалното предаване. Финалният резултат и обратната връзка стават достъпни след приключване на проверката от преподавател.',
        },
        instructors.length > 0 && {
            question: 'Кой проверява работите?',
            answer: `Преподавателите зад матурата — ${joinWithAnd(teachers.map((t) => `${t.name} (${lowerFirst(t.role)})`))}.`,
        },
        {
            question: 'Включена ли е индивидуална консултация?',
            answer: 'Не. Консултацията е отделна възможност след финалния резултат и се заявява и заплаща отделно.',
        },
        exam.has_raffle && {
            question: 'Всеки участник ли получава сборник?',
            answer: 'Не. Сборникът е награда чрез томбола. Всеки записан участник влиза автоматично в томболата, но наградата не е гарантирана.',
        },
    ].filter(Boolean);

    return {
        metaTitle: `${exam.title} | eAcademiaBg`,
        metaDescription: exam.meta_description || exam.description,
        eyebrow: 'Онлайн пробна матура',
        lead: exam.description,
        heroImage: exam.image_url,

        whatIsTitle: 'Какво представлява пробната матура?',
        whatIsText: exam.what_is_text || 'Пробната матура работи като диагностика: показва къде подготовката на детето е стабилна и къде се губят точки — докато има време за реакция.',
        highlights: exam.highlights || [],

        modulesTitle: 'Какво обхваща?',
        modulesSubtitle: coverage,
        modules,
        uploadTitle: 'Как се качват задачите с отворен отговор',
        uploadText: hasHandwritten
            ? `В ${lowerFirst(handwrittenModules)} ученикът решава задачите на лист хартия, снима или сканира решенията и ги прикачва в изпитната система. Снимките се изпращат за проверка от преподавател, затова целият отговор трябва да се вижда четливо.`
            : null,
        uploadImportantNote: 'Важно: Снимките на отговорите трябва да са ясни, добре осветени и напълно четливи. Неясни или размазани снимки могат да попречат на преподавателя да провери и оцени работата.',

        benefitsTitle: 'Какво ще получите?',
        benefits: [
            {
                icon: 'result',
                title: 'Резултат',
                text: 'Автоматично оценяваните задачи показват резултат веднага след финалното предаване. Финалният резултат обединява тази част и оценката на преподавателя.',
            },
            {
                icon: 'review',
                title: 'Проверка от преподавател',
                text: hasHandwritten
                    ? `Задачите с отворен отговор от ${lowerFirst(handwrittenModules)} се проверяват и оценяват от преподавател.`
                    : 'Писмените и отворените задачи се преглеждат и оценяват от преподавател.',
            },
            {
                icon: 'feedback',
                title: 'Финална оценка',
                text: `Обща оценка ${moduleCount > 1 ? `от ${MODULE_COUNT_WORDS[moduleCount] || `${moduleCount}-те`} модула` : 'за матурата'}, кратък коментар от преподавателя и възможност за допълнителна консултация с по-подробни разяснения.`,
            },
        ],

        processTitle: 'Как протича?',
        processSubtitle: 'Стъпките се случват последователно — без фиксиран ден и час.',
        processSteps: [
            { title: 'Регистрация на нашия сайт' },
            { title: 'Купуваш пробната матура' },
            { title: 'Получаваш достъп', text: 'Потвърждение по имейл.' },
            { title: 'Решаваш матурата', text: 'Онлайн, в удобно време.' },
            { title: 'Предаваш отговорите', text: 'Автоматично оценяваните задачи показват резултат веднага.' },
            {
                title: 'Учител оценява работата',
                text: hasHandwritten
                    ? `${handwrittenModules} ${handwrittenNumbers.length === 1 ? 'се проверява' : 'се проверяват'} от преподавател.`
                    : 'Отворените задачи се проверяват от преподавател.',
            },
            { title: 'Получаваш резултат и обратна връзка', text: 'По имейл, след проверката.', highlighted: true },
            { title: 'По желание: заявяваш консултация', optional: true },
        ],

        teachersTitle: 'Учителите зад матурата',
        teachersSubtitle: 'Отворените и писмените задачи не се оценяват автоматично — зад всяка проверка стои преподавател.',
        teachers,
        consultationTitle: 'Допълнителна консултация',
        consultationText: 'След като финалният резултат е готов, можете да заявите отделна индивидуална консултация с преподавател — за анализ на резултата, конкретни насоки и следващи стъпки в подготовката.',
        consultationNote: 'Консултацията може да бъде заявена през „Курсове“ на избрания преподавател.',

        raffle: exam.has_raffle ? {
            title: 'Томбола за сборник',
            text: 'Всеки закупил пробна матура участва автоматично в томбола за сборник или помагало, подбрано от преподавател-експерт.',
            note: 'Сборник не е гарантиран за всеки участник и не е част от базовата покупка.',
        } : null,

        finalCtaTitle: 'Готови ли сте да проверите нивото?',
        finalCtaText: `${coverage ? `${coverage}. ` : ''}Ученикът започва тогава, когато е готов.`,
        soldOutText: 'Местата за тази пробна матура са изчерпани. Моля, опитайте отново по-късно.',

        faqTitle: 'Често задавани въпроси',
        faq,

        purchaseDialogTitle: 'Покупка на пробната матура',
        purchaseDialogText: 'Ще бъдеш пренасочен към защитена външна страница за сигурно плащане. След успешна покупка получавате достъп и инструкции по имейл.',
        purchaseHighlights: [
            coverage,
            hasHandwritten && `Задачи с отворен отговор в ${lowerFirst(handwrittenModules)}, които се решават на лист, снимат и качват`,
            'Без фиксиран ден или час',
            exam.has_raffle && 'Автоматично участие в томболата за сборник',
        ].filter(Boolean),
    };
};
